import React, { useEffect, useState } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import { useCars } from "../context/CarContext";
import { FaArrowLeft, FaGasPump, FaCar, FaUsers, FaSuitcase, FaCalendarAlt, FaTachometerAlt, FaPalette, FaBolt, FaTag } from "react-icons/fa";
import "./CarDetailPage.css";

const CarDetailPage = () => {
  const { id } = useParams();
  const { cars } = useCars();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Simuler un chargement
    setLoading(true);
    
    // Trouver la voiture correspondante
    const foundCar = cars.find((c) => c.id === parseInt(id));
    
    if (foundCar) {
      setCar(foundCar);
      // Titre de la page
      document.title = `${foundCar.name} - AMSEBRID`;
    }
    
    setLoading(false);

    // Nettoyer le titre quand on quitte la page
    return () => {
      document.title = "AMSEBRID - Location de voitures à Bejaia";
    };
  }, [id, cars]);

  const handleGoBack = () => {
    // Définir explicitement le titre avant de naviguer
    document.title = "AMSEBRID - Location de voitures à Bejaia";
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="car-detail-loading">
        <div className="loading-spinner"></div>
        <p>Chargement des détails...</p>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="car-detail-error">
        <h2>Véhicule non trouvé</h2>
        <p>Désolé, nous n'avons pas pu trouver le véhicule demandé.</p>
        <button onClick={handleGoBack} className="back-button">
          <FaArrowLeft /> Retour aux véhicules
        </button>
      </div>
    );
  }

  return (
    <div className="car-detail-page">
      <div className="car-detail-container">
        <button onClick={handleGoBack} className="back-button">
          <FaArrowLeft /> Retour
        </button>
        
        <div className="car-detail-header">
          <h1>{car.name}</h1>
          <div className="car-availability-badge">
            {car.available ? (
              <span className="available">DISPONIBLE</span>
            ) : (
              <span className="unavailable">NON DISPONIBLE</span>
            )}
          </div>
        </div>
        
        <div className="car-detail-content">
          <div className="car-detail-image-container">
            <img src={car.image} alt={car.name} className="car-detail-image" />
          </div>
          
          <div className="car-detail-info">
            <div className="car-detail-price">
              <h2><span className="price-period">à partir de *</span>{car.price} {car.currency || "DZD"} <span className="price-period">/ jour</span></h2>
            </div>
            
            <div className="car-detail-description">
              <h3>Description</h3>
              <p>{car.description}</p>
            </div>
            
            <div className="car-detail-specs">
              <h3>Caractéristiques</h3>
              <div className="specs-grid">
                {car.transmission && (
                  <div className="spec-item">
                    <FaCar className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Transmission</span>
                      <span className="spec-value">{car.transmission}</span>
                    </div>
                  </div>
                )}
                
                {car.fuel && (
                  <div className="spec-item">
                    <FaGasPump className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Carburant</span>
                      <span className="spec-value">{car.fuel}</span>
                    </div>
                  </div>
                )}
                
                {car.power && (
                  <div className="spec-item">
                    <FaBolt className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Puissance</span>
                      <span className="spec-value">{car.power}</span>
                    </div>
                  </div>
                )}
                
                {car.passengers && (
                  <div className="spec-item">
                    <FaUsers className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Places</span>
                      <span className="spec-value">{car.passengers}</span>
                    </div>
                  </div>
                )}
                
                {car.luggage && (
                  <div className="spec-item">
                    <FaSuitcase className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Bagages</span>
                      <span className="spec-value">{car.luggage}</span>
                    </div>
                  </div>
                )}
                
                {car.year && (
                  <div className="spec-item">
                    <FaCalendarAlt className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Année</span>
                      <span className="spec-value">{car.year}</span>
                    </div>
                  </div>
                )}
                
                {car.mileage && (
                  <div className="spec-item">
                    <FaTachometerAlt className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Kilométrage</span>
                      <span className="spec-value">{car.mileage} km</span>
                    </div>
                  </div>
                )}
                
                {car.color && (
                  <div className="spec-item">
                    <FaPalette className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Couleur</span>
                      <span className="spec-value">{car.color}</span>
                    </div>
                  </div>
                )}
                
                {car.category && (
                  <div className="spec-item">
                    <FaTag className="spec-icon" />
                    <div className="spec-content">
                      <span className="spec-label">Catégorie</span>
                      <span className="spec-value">{car.category}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="car-detail-features">
              <h3>Équipements</h3>
              <div className="features-list">
                {car.features && car.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="feature-dot"></span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="car-detail-actions">
              {car.available ? (
                <NavLink 
                  to={`/reservation?voitureId=${car.id}`} 
                  className="reservation-button"
                >
                  Réserver maintenant
                </NavLink>
              ) : (
                <button className="reservation-button disabled" disabled>
                  Non disponible
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetailPage;
